import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SEO from '@/components/base/SEO';
import { categories } from '@/mocks/categories';
import { allComics } from '@/mocks/comics';

const CANONICAL_URL = 'https://www.hanmanzhijia.com';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const comics = useMemo(() => {
    if (!slug) return [];
    return allComics.filter((c) => c.categorySlug === slug);
  }, [slug]);

  const sortedComics = useMemo(() => {
    const list = [...comics];
    if (sortBy === 'popular') return list.sort((a, b) => b.views - a.views);
    if (sortBy === 'latest') return list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return list.sort((a, b) => b.rating - a.rating);
  }, [comics, sortBy]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 韩漫之家`,
    description: category.description,
    url: `${CANONICAL_URL}/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: sortedComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `${CANONICAL_URL}/comic/${comic.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <SEO
        title={`${category.name}漫画`}
        description={`${category.description}。韩漫之家提供丰富多样的${category.name}类韩国漫画，全部免费在线观看。`}
        keywords={`韩国漫画免费,${category.name},韩漫,下拉式阅读`}
        canonicalUrl={`${CANONICAL_URL}/category/${slug}`}
        schema={schema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: category.name },
            ]}
          />

          {/* Category Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          {/* Sort bar */}
          <div className="flex items-center gap-2 mb-8 flex-wrap">
            <span className="text-sm text-foreground-500 mr-2">排序：</span>
            {[
              { key: 'popular' as const, label: '最热门' },
              { key: 'latest' as const, label: '最新更新' },
              { key: 'rating' as const, label: '最高评分' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  sortBy === opt.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Comic Grid */}
          {sortedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 pb-14" data-product-shop>
              {sortedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="normal" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画，敬请期待</p>
            </div>
          )}

          {/* Other categories */}
          <div className="pb-14">
            <h3 className="font-heading text-lg text-foreground-900 mb-4">探索更多分类</h3>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}